import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { UploadSimple, FileCsv } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

export default function CsvImport({ open, onOpenChange, onDone, campaignId }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const upload = async () => {
    if (!file) return;
    if (!campaignId) { toast.error("No campaign selected"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/campaigns/${campaignId}/creators/import`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`${data.inserted} creator(s) imported`);
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Import failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) { setFile(null); setResult(null); } }}>
      <DialogContent data-testid="csv-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Import creators from CSV</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            Add many creators to this campaign at once. New CSV rows default to <b>Non-GST</b> invoice type unless the file specifies otherwise. Recognized columns:{" "}
            <span className="font-mono text-xs">
              Username, Link, Followers, City, State, Language, Gender, Genre, Deliverables,
              Phone, Email, Status (Prospect/Shortlisted/Locked/Live/Cancelled),
              Initial Cost, Final Cost, Script Status, Live Date, Live Link,
              Views, Likes, Comments, Shares, PAN, GST Number, Invoice Type, Notes
            </span>
          </p>

          <label className="flex flex-col items-center justify-center py-10 border-2 border-dashed border-slate-300 rounded-md cursor-pointer hover:border-blue-500 hover:bg-blue-50/40 transition-colors">
            <FileCsv size={32} className="text-slate-400" weight="duotone" />
            <div className="text-sm mt-2 text-slate-700 font-medium">
              {file ? file.name : "Click to choose a CSV file"}
            </div>
            <div className="text-xs text-slate-500 mt-1">Only .csv files</div>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="hidden"
              data-testid="csv-file-input"
            />
          </label>

          {result && (
            <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200 text-sm text-emerald-800" data-testid="csv-import-result">
              Imported <strong>{result.inserted}</strong> creator(s).
              {result.errors?.length > 0 && (
                <div className="mt-2 text-xs text-emerald-700">{result.errors.length} row(s) skipped</div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="csv-cancel-btn">Close</Button>
          <Button onClick={upload} disabled={!file || busy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="csv-import-btn">
            <UploadSimple size={16} className="mr-1.5" />
            {busy ? "Importing…" : "Import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
